import ComponentBase, { ComponentBaseProps } from "../../../components/ComponentBase";
import RupIcons from "../../../RupIcons";
import Utils from "./common/Utils";
import SuggestionEntry from "./common/SuggestionEntry";
import GlobalSearchState, {
	ContentSearchBlock,
	QueryResult,
	SearchBlockOperator,
	SearchBlockType,
	GlobalSearchMode,
	GlobalSearchScope,
	GlobalSearchScopeName
} from "../typings";
import SearchBox from "./SearchBox";

export interface SearchOptionsProps extends ComponentBaseProps {
	readonly state: GlobalSearchState;
	readonly searchBox: SearchBox;
	readonly onClick: (term: ContentSearchBlock) => void;
}

interface SearchOptionsSuggestions {
	readonly mainSuggestions: JQuery[];
	readonly freeText: JQuery;
}

class SearchOptionsBody {
	private _variableBody: JQuery;
	private _constantBody: JQuery;
	private _onEmptied: {(): void}[] = [];
	
	constructor(body: JQuery) {
		this._variableBody = body;
		this._constantBody = $("<div>");
	}

	public onEmptied(cb: {(): void}): SearchOptionsBody {
		this._onEmptied.push(cb);
		return this;
	}

	public get elements(): JQuery[] {
		return [this._variableBody, this._constantBody];
	}

	public get hasFreeText(): boolean {
		return this._constantBody.children().length > 0;
	}

	public append(body: string | JQuery | SearchOptionsSuggestions) {
		if ((body as JQuery).jquery || typeof(body) === "string") {
			this._variableBody
				.empty()
				.append(body as string | JQuery);
		} else {
			body = body as SearchOptionsSuggestions;
			this._variableBody
				.empty()
				.append(body.mainSuggestions);
			if (body.freeText) {
				this._constantBody
					.empty()
					.append(body.freeText);
			}
		}
	}

	public empty() {
		this._variableBody.empty();
		this._constantBody.empty();

		for (const c of this._onEmptied) {
			c();
		}
	}
}

export default class SearchOptions extends ComponentBase<SearchOptionsProps>{

	private body: SearchOptionsBody;
	private suggestions: SuggestionEntry[] = [];
	private buttons: JQuery[] = [];
	private buttonOptions: JQuery[] = [];
	private currentLookupRequest: JQueryXHR;
	private previousHeight = 0;
	private searchMode: GlobalSearchMode;
	private inputText: string;
	private icons = {
		[SearchBlockType.FreeText]: RupIcons.text,
		[SearchBlockType.Synonym]: RupIcons.tag,
		[SearchBlockType.Descriptor]: RupIcons.tag,
		[SearchBlockType.Vocab]: RupIcons.tag,
		[SearchBlockType.Operator]: RupIcons.operator,
		isPrev: RupIcons.recent,
		isSuggest: RupIcons.search,
	};
	private searchScope: GlobalSearchScope;
	private searchModeName: string;
	private ignoreKeys: string[] = ['sortby', 'colids', 'daterange'];

	constructor(props: SearchOptionsProps) {
		super(props);
		this.searchMode = props.state.searchMode;
		this.searchScope = props.state.searchScope;
		this.inputText = props.searchBox.inputField?.val();
		this.searchModeName = Utils.getAnalyticSearchMode(props.state);
	}

	public remove(): void {
		this.container?.remove();
	}

	public get options(): JQuery[] {
		return this.buttonOptions;
	}

	public get lookupRequest(): JQueryXHR {
		return this.currentLookupRequest;
	}

	public set lookupRequest(currentLookupRequest: JQueryXHR) {
		this.currentLookupRequest = currentLookupRequest;
	}

	public updateSearchOptions(value?: string): void {
		if (value?.length > 1) {
			this.fillOptionsBody(value);
			return;
		}
		const operator = Utils.getOperator(value || "");
		this.body.empty();
		if (operator?.special) {
			this.fillOptionsBody(operator);
		} else {
			this.stopRequest();
		}
	}

	public dispose(): void {
		this.stopRequest();
		this.clearSuggestions();
	}

	private clearSuggestions(): void {
		for (const s of this.suggestions) {
			s.dispose();
		}
		this.suggestions = [];
	}

	private renderBody(): void {
		this.body = new SearchOptionsBody($("<div>")
			.addClass("rup-global-search__options-body globalSearch__optionsBody"))
			.onEmptied(() => this.clearSuggestions());
	}

	protected renderInternal(): JQuery {
		this.renderBody();
		const footer = this.props.state.noAdvancedSearch ? "" : this.renderOptionsFooter();
		this.containerInternal = $("<div>")
			.addClass("rup-global-search__options-container")
			.append(this.body.elements)
			.append(this.renderHints())
			.append(footer);

		return this.containerInternal;
	}

	private fillOptionsBody(specialOperator?: string | SearchBlockOperator) {
		if (specialOperator && typeof specialOperator !== "string") {
			if (this.searchMode !== GlobalSearchMode.Default) {
				this.addSpecialOperatorOption(specialOperator);
			}
			return;
		}

		if (this.searchMode === GlobalSearchMode.Default || this.searchMode === GlobalSearchMode.FullText) {
			const method = "SearchesQuery";
			const data = { "search": specialOperator, "source": this.props.state.selectedSideItemValue, "noPreviousSearches": false };
			this.lookup(data, method);
		} else if (this.searchMode === GlobalSearchMode.SemanticSearch) {
			const method = "TermsQuery";
			const data = { "search": specialOperator };
			this.lookup(data, method);
		}
	}

	private addSpecialOperatorOption(specialOperator: SearchBlockOperator) {
		this.addOperatorButton(specialOperator);
		this.body
			.append(this.buttons.length ? this.renderOptionsList() : "");
	}

	private addOperatorButton(specialOperator: SearchBlockOperator) {
		const term = this.getDefaultOperator(specialOperator);
		const button = this.renderOptionsButton(term);
		if (button && !this.props.searchBox.isEditMode) {
			this.buttons.push(button);
		}
	}

	private getDefaultOperator(operator: SearchBlockOperator): QueryResult {
		return {
			DescriptorId: null,
			DisplayName: operator.special ? operator.name : `${operator.name} (${GlobalSearchLabels.Operator})`,
			Name: operator.text, 
			SynName: null,
			Type: SearchBlockType.Operator,
			Value: null,
			Vocab: null,
			VocabularyId: null
		};
	}

	private getDefaultText(block: ContentSearchBlock): QueryResult {
		return {
			DescriptorId: null,
			DisplayName: `${block.text} (${GlobalSearchLabels.FreeTextQuery})`,
			Name: null,
			SynName: null,
			Type: SearchBlockType.FreeText,
			Value: block.text,
			Vocab: null,
			VocabularyId: null
		};
	}
	
	private showIndicator(): void {
		const indicator = this.containerInternal.find("div.rup-global-search__loading-indicator");
		if (indicator && indicator.length) {
			return;
		}
		this.body.append(this.renderIndicator());
	}

	public stopRequest(): void {
		if (this.currentLookupRequest == null) {
			return;
		}
		this.currentLookupRequest.abort();
		this.currentLookupRequest = null;
	}

	private lookup(data: unknown, method: string) {
		const base = this.get_serviceURL();
		this.stopRequest();
		this.showIndicator();

		const searchString = data["search"];

		this.currentLookupRequest = $.ajax({
			type: "POST",
			url: `${base}Lookup/${method}`,
			data: JSON.stringify(data),
			contentType: "application/json; charset=utf-8",
			dataType: "json",

			success: (data) => {
				this.clearSuggestions();
				this.suggestions = [];
				this.buttons = [];

				if (data.SuggestedSearches) {
					const logicalOperator = Utils.getOperatorLike(searchString);
					if (logicalOperator) {
						this.suggestions.push(this.renderOptionsButtonWithHighlight(
							this.getDefaultOperator(logicalOperator)));
					}

					if (data.SuggestedSearches?.length || data.PreviousSearches?.length) {
						this.suggestions.push(...(data.SuggestedSearches || []).map(item => this.renderOptionsButtonWithHighlight(item)));
						this.suggestions.push(...(data.PreviousSearches || []).map(item => this.renderOptionsButtonWithHighlight(item, true)));
					}

					const freeTextItem = this.renderOptionsButtonWithHighlight(
						this.getDefaultText(this.props.searchBox.defaultTextBlock));
					freeTextItem.button.attr("is-free-text", "1");
					this.suggestions.push(freeTextItem);
				} else {
					this.suggestions.push(...data.map(item => this.renderOptionsButtonWithHighlight(item)));
				}

				this.suggestions = this.suggestions.filter(item => item);
				for (const suggestion of this.suggestions) {
					this.buttons.push(suggestion.button);
					suggestion.highlight(searchString);
				}

				this.body
					.append(this.buttons.length ? this.renderOptionsList() : "");

				this.animateBody();
			},
		});
	}

	private get_serviceURL(): string {
		return RFE.combineRoot("/services", true);
	}

	private renderIndicator(): JQuery {
		return $("<div>")
			.addClass("rup-global-search__loading-indicator")
			.append(
				$("<span>")
					.addClass("rup-loader")
					.append(
						$("<span>")
							.addClass("rup-visually-hidden")
							.text("Loading...")
					)
					.attr("role", "status")
			);
	}

	private renderOptionsList(): SearchOptionsSuggestions {
		let newSuggestions: JQuery[];
		let prevSuggestions: JQuery[];
		let freeTextSuggestion: JQuery[] = [];
		
		if (this.searchMode !== GlobalSearchMode.SemanticSearch) {
			newSuggestions = this.buttons
				.filter(button => (button.attr("isPrev") !== "true") && (button.attr("is-free-text") !== "1"));
			prevSuggestions = this.buttons
				.filter(button => (button.attr("isPrev") === "true") && (button.attr("is-free-text") !== "1"));
			freeTextSuggestion = this.buttons
				.filter(button => button.attr("is-free-text") === "1");
		} else {
			newSuggestions = this.buttons.filter(button => +button.attr("termType") !== SearchBlockType.FreeText);
			freeTextSuggestion = this.buttons.filter(button => +button.attr("termType") === SearchBlockType.FreeText);
		}

		this.buttonOptions = this.buttons.slice();
		this.buttons = [];
		return {
			mainSuggestions: [this.renderOptionListPart(newSuggestions || []),
				this.renderOptionListPart(prevSuggestions || [])],
			freeText: freeTextSuggestion ? this.renderOptionListPart(freeTextSuggestion, !this.body.hasFreeText) : null
		};
	}

	private renderOptionListPart(options: JQuery[], animate = true): JQuery {
		return $("<div>")
			.append(options)
			.attr({
				role: "listbox"
			})
			.children()
			.each((index, elem: HTMLElement) => {
				if (!(elem.animate && animate)) {
					return;
				}
				elem.style.opacity = '0';
				elem.animate([
					{ opacity: 0, transform: 'translateY(-8px)' },
					{ opacity: 1, transform: 'translateY(0)' }
				], {
					duration: 100,
					delay: 75 + 32 * index,
					iterations: 1
				}).onfinish = () => {
					elem.style.opacity = '';
				};
			})
			.parent();
	}

	private renderOptionsButtonInternal(term: QueryResult, isPrevSearches = false): JQuery {
		let operator: SearchBlockOperator;

		for (const prop of ["DisplayName", "Name", "Value", "SynName"]) {
			term[prop] = term[prop]?.replace(/\s{2,}/g, " ");
		}

		if (term.Type === SearchBlockType.Operator) {
			operator = Utils.getOperator(term.Name);
			if (!this.props.searchBox.validateOperator(operator)) {
				return null;
			}
		}

		const icon = this.icons[term.Type] ?? (isPrevSearches ? this.icons["isPrev"] : this.icons["isSuggest"]);
		return $("<button>")
			.addClass("rup-global-search__selectable-option globalSearch__button")
			.append(icon)
			.attr({
				role: "Option",
				type: "button",
				termType: term.Type,
				isPrev: isPrevSearches
			})
			.mousedown(() => false)
			.click((e) => {
				e.preventDefault();

				const searchId = term.Id;
				if (searchId) {
					this.doPrevSearch(searchId);
				} else {
					this.body.empty();
					const searchParameters = {
						type: term.Type || SearchBlockType.FreeText,
						text: term.Value || term.Name,
						synName: term.SynName,
						descriptorName: term.Name,
						opType: operator?.type,
						vocab: term.Vocab,
						descriptor: { descriptorId: term.DescriptorId, vocabularyId: term.VocabularyId }
					};
					this.props.onClick(searchParameters);
				}
			});
	}

	private renderOptionsButton(term: QueryResult, isPrevSearches = false): JQuery {
		const result: JQuery = this.renderOptionsButtonInternal(term, isPrevSearches);
		if (result) {
			return result.append($("<span>").text(term.DisplayName || term.Name));
		}
	}

	private renderOptionsButtonWithHighlight(term: QueryResult, isPrevSearches = false): SuggestionEntry {
		const result: JQuery = this.renderOptionsButtonInternal(term, isPrevSearches);
		if (result) {
			return new SuggestionEntry(result, term);
		}
		return null;
	}

	private doPrevSearch(id: number) {
		if (id < 0)
			return;

		const base = this.get_serviceURL();
		const data = { "SearchId": id };
		$.ajax({
			type: "POST",
			url: base + "Lookup/FetchSearch",
			data: JSON.stringify(data),
			contentType: "application/json; charset=utf-8",
			dataType: "json",

			success: (response) => {
				if (this.search) {
					this.search(response);
				}
			}
		});
	}

	private getAnalyticSearchMode(searchMethod: RFE.ContentSearchMethod): string {
		if (!searchMethod) {
			return 'unknown';
		}

		switch (searchMethod) {
			case RFE.ContentSearchMethod.MultiSearch:
				return 'eds';
			case RFE.ContentSearchMethod.Semantic:
			case RFE.ContentSearchMethod.Boolean:
			case RFE.ContentSearchMethod.AdvancedSemantic:
			case RFE.ContentSearchMethod.AdvancedBoolean:
			case RFE.ContentSearchMethod.Lucene:
			case RFE.ContentSearchMethod.Library:
				return 'semantic';
		}

		return 'unknown';
	}

	private getAnalyticSearchScope(searchMethod: RFE.ContentSearchMethod): string {
		if (searchMethod === RFE.ContentSearchMethod.Library || this.searchScope === GlobalSearchScope.MyLibrary) {
			return GlobalSearchScopeName[GlobalSearchScope.MyLibrary];
		}

		return GlobalSearchScopeName[GlobalSearchScope.Global];
	}

	private getAnalyticSearchPhrase(searchData: string): string {
		const ignoreKeys = this.ignoreKeys;
		return (searchData || '')
			.split('&')
			.map(paramItem => (paramItem || '').split('='))
			.filter(item => item?.length > 1)
			.map(([key, value]) => {
				if (ignoreKeys.indexOf(key.toLowerCase()) >= 0) {
					return null;
				}

				return `${value}|C:${key}`;
			})
			.filter(Boolean)
			.join(' ');
	}

	/* eslint-disable  @typescript-eslint/no-explicit-any */
	private search(response: any): void {
		const searchData = response.SearchData;
		const searchId = response.SearchId;
		const searchMethod = response.SearchMethod;

		if (!searchData.length) {
			return;
		}

		const logValue = this.getAnalyticSearchPhrase(searchData);
		gaLogAction?.(
			`globalsearch:${this.getAnalyticSearchMode(searchMethod)}`,
			`globalsearch:${this.getAnalyticSearchScope(searchMethod)}`,
			'search phrase',
			logValue);

		if (searchMethod === RFE.ContentSearchMethod.MultiSearch) {
			mainNavigateContent("order/MultiSearch.aspx?savedsearch=" + searchId);
			return;
		}

		let url = ((searchMethod === RFE.ContentSearchMethod.Library) ? RFE.LibraryPage : RFE.SearchPage) + "?";

		let query = "q=true";
		query += "&" + searchData;

		url += query;
		mainNavigateContent(url);
	}

	private renderHints(): JQuery {
		const hintsBlock = $("<div>")
			.addClass("rup-global-search__hints")
			.append(this.renderHint("to navigate", RupIcons.navigateUp, RupIcons.navigateDown))
			.append(this.renderHint("to select", RupIcons.navigateSelect))
			.append(this.renderHint("to dismiss", RupIcons.navigateEsc))
			.attr({
				role: "presentation"
			});

		return $("<div>")
			.addClass("rup-global-search__hints-container")
			.append(hintsBlock);
	}

	private renderHint(text: string, ...icons: string[]): JQuery {
		return $("<div>")
			.addClass("rup-global-search__hints")
			.append(icons.map(icon => this.renderHintIcon(icon)))
			.append(this.renderHintText(text));
	}

	private renderHintIcon(icon: string): JQuery {
		return $("<span>")
			.addClass("rup-global-search__hints_icon")
			.append(icon);
	}

	private renderHintText(text: string): JQuery {
		return $("<span>")
			.addClass("rup-global-search__hints_text")
			.text(text);
	}

	private renderOptionsFooter(): JQuery {
		const button = $("<button>")
			.addClass("rup-btn rup-btn--right")
			.text(this.props.state.advancedSearchButtonText)
			.attr({
				type: "button"
			})
			.click(() => {
				this.props.state.handlers.advancedSearch();
			});

		return $("<div>")
			.addClass("rup-global-search__options-footer")
			.append(button);
	}

	public animateBody(duration = 250): void {
		const container = this.body.elements[0][0];
		const newHeight = container.getBoundingClientRect().height;
		container.style.pointerEvents = 'none';
		container.style.height = `${this.previousHeight}px`;
		container.style.overflow = 'hidden';
		container.animate([
			{ height: `${this.previousHeight}px` },
			{ height: `${newHeight}px` }
		], {
			duration: duration,
			iterations: 1
		}).onfinish = () => {
			container.style.height = '';
			container.style.pointerEvents = '';
			container.style.overflow = '';
		};
		this.previousHeight = newHeight;
	}
}
